import { lazy, LazyExoticComponent, type ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthGuard } from '../guards/authGuard';
import { type AppRouteConfig, type LazyRouteModule } from '../route';

const lazyRouteComponentCache = new Map<
  AppRouteConfig['lazyImport'],
  LazyExoticComponent<ComponentType<object>>
>();

function getLazyRouteComponent(lazyImport: () => Promise<LazyRouteModule>) {
  const cachedComponent = lazyRouteComponentCache.get(lazyImport);

  if (cachedComponent) {
    return cachedComponent;
  }

  const RouteComponent = lazy(lazyImport);
  lazyRouteComponentCache.set(lazyImport, RouteComponent);
  return RouteComponent;
}

export function LazyLoadRoute({ route }: { route: AppRouteConfig }) {
  const decision = useAuthGuard(route);

  if (!decision.canAccess) {
    return <Navigate replace to={decision.redirectTo || '/'} />;
  }

  if (route.redirectTo) {
    return <Navigate replace to={route.redirectTo} state={route.redirectState} />;
  }

  if (!route.lazyImport) {
    return null;
  }

  const RouteComponent = getLazyRouteComponent(route.lazyImport);
  return <RouteComponent />;
}
